import type { Address } from "viem"
import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

import { erc20Abi } from "@/domains/abis"

export type WaitForAllowanceOptions = {
  pollIntervalMs?: number
  timeoutMs?: number
  maxTries?: number
}

const DEFAULT_POLL_INTERVAL_MS = 2000
const DEFAULT_TIMEOUT_MS = 120_000
const DEFAULT_MAX_TRIES = 10

/**
 * Polls the ERC20 allowance(owner, spender) until it is >= requiredAmountWei, or maxTries or timeout is reached.
 * Use after sending an approval tx so addLiquidity (or similar) is not called before the chain has updated.
 */
export async function waitForAllowance({
  config,
  chainId,
  contractAddress,
  ownerAddress,
  spenderAddress,
  requiredAmountWei,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  maxTries = DEFAULT_MAX_TRIES,
}: {
  config: Config
  chainId: number
  contractAddress: Address
  ownerAddress: Address
  spenderAddress: Address
  requiredAmountWei: bigint
} & WaitForAllowanceOptions): Promise<void> {
  const deadline = Date.now() + timeoutMs

  const readAllowance = async (): Promise<bigint> => {
    const result = await readContract(config, {
      abi: erc20Abi,
      address: contractAddress,
      functionName: "allowance",
      args: [ownerAddress, spenderAddress],
      chainId,
    })
    return BigInt((result as bigint) ?? 0)
  }

  let lastAllowance = 0n
  for (let tryCount = 0; tryCount < maxTries; tryCount++) {
    lastAllowance = await readAllowance()
    if (lastAllowance >= requiredAmountWei) return

    if (Date.now() >= deadline) {
      throw new Error(
        `Allowance did not reach ${requiredAmountWei.toString()} within ${timeoutMs}ms (current: ${lastAllowance.toString()})`,
      )
    }

    if (tryCount < maxTries - 1) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs))
    }
  }

  throw new Error(
    `Allowance did not reach ${requiredAmountWei.toString()} after ${maxTries} tries (current: ${lastAllowance.toString()})`,
  )
}
